import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { apiErr } from "../../lib/api";
import { useLang } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { PhaseIntro, AddInput } from "../../components/phase-common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Flame, MapPin, X, CheckCircle2, CreditCard } from "lucide-react";

export default function Phase5({ org, refresh, isAdmin }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [word, setWord] = useState(org.core_emotion || "");
  const [busy, setBusy] = useState(false);
  const proposals = org.core_emotion_proposals || [];

  const propose = async (w) => {
    try { await api.post("/org/core-emotion/propose", { word: w }); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delProp = async (id) => { await api.delete(`/org/core-emotion/propose/${id}`); refresh(); };

  const setCore = async () => {
    try { await api.patch("/org/core-emotion", { word: word.trim() }); toast.success(t("saved")); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: "consultant", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PhaseIntro phase={5} title={t("p5_title")} intro={t("p5_intro")} />

      <div className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-card p-8 text-center">
        <Flame className="mx-auto h-8 w-8 text-accent" />
        <p className="mt-2 text-sm font-semibold uppercase tracking-widest text-muted-foreground">{t("core_emotion_set")}</p>
        {org.core_emotion ? (
          <motion.p initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            data-testid="core-emotion-word" className="mt-2 font-display text-5xl font-bold text-primary sm:text-6xl">
            {org.core_emotion}
          </motion.p>
        ) : (
          <p className="mt-2 font-display text-2xl italic text-muted-foreground">{t("not_set_yet")}</p>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <h3 className="font-display text-lg font-semibold text-primary">{t("propose_word")}</h3>
        <AddInput testId="p5-propose" label={t("add")}
          placeholder={lang === "nl" ? "Eén positief woord…" : "One positive word…"} onAdd={propose} />
        <div className="flex flex-wrap gap-2">
          {proposals.map((p) => (
            <span key={p.id} className="group flex items-center gap-2 rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold text-secondary-foreground">
              {p.word}
              <button onClick={() => delProp(p.id)} data-testid={`prop-del-${p.id}`} aria-label="remove">
                <X className="h-3.5 w-3.5 text-destructive" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* TTPA consultant */}
      <div className="rounded-2xl border border-accent/30 bg-accent/5 p-6 space-y-4">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <img src="/ttpa-seal.jpg" alt="Think Two Products Ahead Licensed TTPA Consultant — Seal of Quality"
            data-testid="ttpa-seal" className="h-24 w-auto shrink-0 rounded-md shadow-sm" />
          <div>
            <h3 className="font-display text-xl font-semibold text-primary">
              {lang === "nl" ? "Think Two Products Ahead Licensed TTPA Consultant" : "Think Two Products Ahead Licensed TTPA Consultant"}
            </h3>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">Seal of Quality</p>
          </div>
        </div>
        <p className="text-sm leading-relaxed text-foreground">{t("consultant_block")}</p>
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> {t("consultant_price")}
        </p>
        {org.consultant_paid ? (
          <p data-testid="consultant-booked" className="flex items-center gap-2 font-semibold text-emerald-700">
            <CheckCircle2 className="h-5 w-5" /> {t("consultant_booked")}
          </p>
        ) : (
          <Button data-testid="book-consultant-btn" onClick={book} disabled={busy} className="rounded-full">
            <CreditCard className="mr-2 h-4 w-4" /> {t("book_consultant")}
          </Button>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-3">
        <h3 className="font-display text-lg font-semibold text-primary">{t("admin_set_core")}</h3>
        {!isSuper && (
          <p className="rounded-lg bg-secondary px-3 py-2 text-sm font-medium text-muted-foreground" data-testid="superadmin-only-note">
            {t("superadmin_only")}
          </p>
        )}
        <div className="flex gap-2">
          <Input data-testid="admin-core-input" value={word} onChange={(e) => setWord(e.target.value)}
            disabled={!isSuper} placeholder={lang === "nl" ? "Eén woord" : "One word"} className="rounded-full disabled:opacity-60" />
          <Button data-testid="admin-core-set" onClick={setCore} disabled={!isSuper} className="rounded-full">{t("set")}</Button>
        </div>
      </div>
    </div>
  );
}
